import React, { ReactNode, createContext, useContext, useMemo, useState } from 'react';
import { createNetworkStatusNotifier } from 'react-apollo-network-status';
import { createClient } from 'graphql-ws';
import { ApolloClient, HttpLink, from, InMemoryCache, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { environment } from '../environment';
import { useAuth } from '../hooks/auth/use-auth';
import { useErrors } from '../hooks/errors/use-errors';
import { useSentry } from '../hooks/sentry/use-sentry';
import { GraphqlState } from '../types/grapql';

const { link: networkStatusNotifierLink } = createNetworkStatusNotifier();

type GraphqlProviderProps = {
  children: ReactNode;
};

const initialState: GraphqlState = {
  client: new ApolloClient({
    cache: new InMemoryCache(),
  }),
  API_URL: environment.API_URL || '',
  LAVVA_MANAGER: environment.API_URL ? `${environment.API_URL}graphql/` : '',
  updateBrokerUrl: () => null,
  reset: () => null,
  handleRefresh: () => null,
};

export const GraphqlContext = createContext(initialState);

export const useGraphqlContext = (): GraphqlState => useContext(GraphqlContext);

export const GraphqlProvider: React.FC<GraphqlProviderProps> = ({ children }) => {
  const { keycloak } = useAuth();
  const { handleGraphQLError } = useErrors();
  const [brokerUrl, setBrokerUrl] = useState<string>(initialState.API_URL);
  const [refresh, setRefresh] = useState<number>(0);
  const { setSentryTags } = useSentry();

  const API_URL = useMemo(() => {
    return brokerUrl.endsWith('/') ? brokerUrl : `${brokerUrl}/graphql/`;
  }, [brokerUrl]);

  const LAVVA_MANAGER = useMemo(() => API_URL.replace('graphql/', ''), [API_URL]);

  const authLink = setContext(async ({ operationName }, { headers }) => {
    const token = await Promise.resolve(keycloak.token);
    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : '',
        'APOLLO-QUERY-NAME': operationName,
        'X-Installation-Subtype': environment.INTEGRATION_DEFAULT,
      },
    };
  });

  const reset = async () => {
    await keycloak.updateToken(-1);
    await client.resetStore();

    await client.setLink(from([onError((error) => handleGraphQLError(error)), link]));
  };

  const handleRefresh = () => setRefresh((prev) => prev + 1);

  const wsLink = useMemo(() => {
    const client = createClient({
      url: (location.protocol === 'http:' ? API_URL.replace('https', 'ws') : API_URL.replace('https', 'wss')) || '',
      retryAttempts: Infinity, // Infinite reconnection attempts
      shouldRetry: () => true, // Always retry on disconnect
      disablePong: true,
      connectionParams: async () => {
        const token = await Promise.resolve(keycloak.token);
        return { Authorization: `Bearer ${token}` };
      },
      onNonLazyError: async (error) => {
        if (error) {
          await reset();
          window.location.reload();
        }
      },
    });

    return new GraphQLWsLink(client);
  }, [API_URL, keycloak, refresh]);

  const link = useMemo(() => {
    return split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      networkStatusNotifierLink.concat(
        authLink.concat(new HttpLink({ uri: (context) => `${API_URL}?opname=${context.operationName}` })),
      ),
    );
  }, [wsLink]);

  const client = useMemo(() => {
    return new ApolloClient({
      link: from([onError((error) => handleGraphQLError(error)), link]),
      cache: new InMemoryCache({
        typePolicies: {
          UserProfile: {
            keyFields: ['userId'],
          },
          Query: {
            fields: {
              userProfile: {
                merge(_, incoming) {
                  return incoming;
                },
              },
            },
          },
        },
      }),
      defaultOptions: {
        mutate: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'ignore',
        },
      },
      devtools: {
        enabled: import.meta.env.MODE === 'development',
      },
    });
  }, [link]);

  keycloak.onAuthRefreshSuccess = async () => {
    await client.resetStore();
  };

  keycloak.onAuthRefreshError = async () => window.location.reload();
  keycloak.onAuthError = async () => window.location.reload();

  const updateBrokerUrl = (url: string) => {
    setBrokerUrl(url);
    setSentryTags({ brokerUrl: url });
  };

  const values = {
    client,
    API_URL,
    LAVVA_MANAGER,
    updateBrokerUrl,
    reset,
    handleRefresh,
  };

  return <GraphqlContext.Provider value={values}>{children}</GraphqlContext.Provider>;
};
